import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell, SaveButton } from "@/components/admin/AdminShell";
import { Text, Area, ListArea } from "@/components/admin/Field";
import { MediaPicker } from "@/components/admin/MediaPicker";

function Section({ title, children }) {
  return (
    <div className="bg-ivory border border-espresso/10 rounded-md p-7 mb-6">
      <h2 className="font-serif text-2xl text-espresso mb-6">{title}</h2>
      <div className="grid md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

export default function HomeEditor() {
  const [d, setD] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/home").then((r) => setD(r.data)).catch(() => {});
  }, []);

  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const slides = d?.hero_slides || [];
  const setSlide = (i, v) => setD((p) => { const s = [...(p.hero_slides || [])]; s[i] = v; return { ...p, hero_slides: s }; });
  const addSlide = () => setD((p) => ({ ...p, hero_slides: [...(p.hero_slides || []), ""] }));
  const delSlide = (i) => setD((p) => ({ ...p, hero_slides: (p.hero_slides || []).filter((_, idx) => idx !== i) }));
  const patientImgs = d?.hero_patient_images || [];
  const setPatientImg = (i, v) => setD((p) => { const s = [...(p.hero_patient_images || [])]; s[i] = v; return { ...p, hero_patient_images: s }; });
  const addPatientImg = () => setD((p) => ({ ...p, hero_patient_images: [...(p.hero_patient_images || []), ""] }));
  const delPatientImg = (i) => setD((p) => ({ ...p, hero_patient_images: (p.hero_patient_images || []).filter((_, idx) => idx !== i) }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/content/home", d);
      toast.success("Homepage updated and published");
    } catch (e) {
      toast.error("Could not save. Please sign in again.");
    } finally {
      setSaving(false);
    }
  };

  if (!d) return <AdminShell title="Homepage"><div className="text-taupe">Loading…</div></AdminShell>;

  return (
    <AdminShell title="Homepage" subtitle="Everything on your home screen" actions={<SaveButton onClick={save} saving={saving} label="Publish Changes" />}>
      <Section title="Hero — Video & Poster">
        <MediaPicker label="Desktop Hero Video" value={d.hero_video_url} onChange={(v) => set("hero_video_url", v)} accept="video/*" kind="video" recommended="MP4, 1920×1080, under 15MB" testid="home-hero-video" />
        <MediaPicker label="Hero Video (WebM fallback)" value={d.hero_video_webm_url} onChange={(v) => set("hero_video_webm_url", v)} accept="video/webm,video/*" kind="video" recommended="WebM/VP9 — plays where MP4/H.264 can't" testid="home-hero-webm" />
        <MediaPicker label="Mobile Hero Video (optional)" value={d.hero_mobile_video_url} onChange={(v) => set("hero_mobile_video_url", v)} accept="video/*" kind="video" recommended="MP4, 1080×1920 vertical" testid="home-hero-mobile-video" />
        <MediaPicker label="Fallback Poster Image" value={d.hero_poster} onChange={(v) => set("hero_poster", v)} recommended="2000×1200 JPG" testid="home-hero-poster" />
        <div className="md:col-span-1 text-sm text-taupe self-center">If no video is uploaded, the poster image is shown with a slow cinematic zoom — so your hero always looks intentional.</div>
      </Section>

      <div className="bg-ivory border border-espresso/10 rounded-md p-7 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-2xl text-espresso">Hero Background Slideshow</h2>
          <button onClick={addSlide} className="text-xs uppercase tracking-[0.14em] border border-espresso/20 rounded-full px-4 py-2 hover:bg-linen" data-testid="home-add-slide">+ Add Slide</button>
        </div>
        <p className="text-sm text-taupe mb-6">Shown as a slow cinematic crossfade when no video is uploaded. Slides play in order — e.g. soft-tissue care, a smiling patient, children, a family.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {slides.map((s, i) => (
            <div key={i}>
              <MediaPicker label={`Slide ${i + 1}`} value={s} onChange={(v) => setSlide(i, v)} recommended="2000×1200 JPG" testid={`home-hero-slide-${i}`} />
              <button onClick={() => delSlide(i)} className="mt-2 text-xs text-destructive/70 hover:text-destructive" data-testid={`home-del-slide-${i}`}>Remove slide</button>
            </div>
          ))}
          {slides.length === 0 && <p className="text-taupe text-sm">No slides yet — add a few images or upload a hero video above.</p>}
        </div>
      </div>

      <div className="bg-ivory border border-espresso/10 rounded-md p-7 mb-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-2xl text-espresso">Hero Reveal Slideshow</h2>
          <button onClick={addPatientImg} className="text-xs uppercase tracking-[0.14em] border border-espresso/20 rounded-full px-4 py-2 hover:bg-linen" data-testid="home-add-patient-img">+ Add Photo</button>
        </div>
        <p className="text-sm text-taupe mb-6">After the hero video plays through, or once a visitor scrolls, it cross-fades into this bright, joyful slideshow — smiling patients, happy families and children. Each photo warms gently into full colour. They play in order, so lead with your strongest image.</p>
        <div className="grid md:grid-cols-3 gap-5">
          {patientImgs.map((s, i) => (
            <div key={i}>
              <MediaPicker label={`Photo ${i + 1}`} value={s} onChange={(v) => setPatientImg(i, v)} recommended="1200×1500 or square JPG" testid={`home-hero-patient-${i}`} />
              <button onClick={() => delPatientImg(i)} className="mt-2 text-xs text-destructive/70 hover:text-destructive" data-testid={`home-del-patient-${i}`}>Remove photo</button>
            </div>
          ))}
          {patientImgs.length === 0 && <p className="text-taupe text-sm">No patient photos yet — the video will simply loop. Add a few to enable the reveal.</p>}
        </div>
      </div>

      <Section title="Hero — Text & Buttons">
        <Text label="Eyebrow" value={d.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} testid="home-hero-eyebrow" />
        <Text label="Headline" value={d.hero_headline} onChange={(v) => set("hero_headline", v)} testid="home-hero-headline" />
        <Area label="Subheading" value={d.hero_subheading} onChange={(v) => set("hero_subheading", v)} rows={2} testid="home-hero-sub" />
        <div />
        <Text label="Primary Button" value={d.hero_cta_primary} onChange={(v) => set("hero_cta_primary", v)} testid="home-hero-cta1" />
        <Text label="Secondary Button" value={d.hero_cta_secondary} onChange={(v) => set("hero_cta_secondary", v)} testid="home-hero-cta2" />
      </Section>

      <Section title="Story Section">
        <Text label="Eyebrow" value={d.story_eyebrow} onChange={(v) => set("story_eyebrow", v)} />
        <div />
        <Text label="Headline Line 1" value={d.story_headline_1} onChange={(v) => set("story_headline_1", v)} />
        <Text label="Headline Line 2 (italic)" value={d.story_headline_2} onChange={(v) => set("story_headline_2", v)} />
        <Area label="Body" value={d.story_body} onChange={(v) => set("story_body", v)} rows={4} />
        <MediaPicker label="Story Image" value={d.story_image} onChange={(v) => set("story_image", v)} recommended="1200×1500 (4:5)" testid="home-story-image" />
      </Section>

      <Section title="Treat the Chain Callout">
        <Text label="Headline Line 1" value={d.chain_headline_1} onChange={(v) => set("chain_headline_1", v)} />
        <Text label="Headline Line 2 (italic)" value={d.chain_headline_2} onChange={(v) => set("chain_headline_2", v)} />
        <Text label="Subline 1" value={d.chain_sub_1} onChange={(v) => set("chain_sub_1", v)} />
        <Text label="Subline 2" value={d.chain_sub_2} onChange={(v) => set("chain_sub_2", v)} />
      </Section>

      <Section title="Patient Goals">
        <Text label="Eyebrow" value={d.goals_eyebrow} onChange={(v) => set("goals_eyebrow", v)} />
        <Text label="Headline" value={d.goals_headline} onChange={(v) => set("goals_headline", v)} />
        <ListArea label="Rotating Goal Statements" value={d.goals} onChange={(v) => set("goals", v)} help="One statement per line" testid="home-goals" />
        <div className="space-y-5">
          <Text label="Goals Button" value={d.goals_cta} onChange={(v) => set("goals_cta", v)} />
          <MediaPicker label="Closing CTA Background" value={d.family_image} onChange={(v) => set("family_image", v)} recommended="2000×1200 JPG" testid="home-family-image" />
        </div>
      </Section>

      <div className="flex justify-end"><SaveButton onClick={save} saving={saving} label="Publish Changes" /></div>
    </AdminShell>
  );
}
